import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { STALE_TIME_MS } from '@/lib/constants'
import type { TrackerPolygon, ZoneType } from '@/types'

// =============================================================================
// useTrackers — Tracker polygons hook
// =============================================================================

interface TrackerFilters {
  parcelNumber?: number | null
  zone?:         ZoneType | null
  isLabeled?:    boolean | null
  page?:         number
  pageSize?:     number
}

export function useTrackers(filters: TrackerFilters = {}) {
  const {
    parcelNumber = null,
    zone         = null,
    isLabeled    = null,
    page         = 0,
    pageSize     = 200,
  } = filters

  const trackersQuery = useQuery<TrackerPolygon[]>({
    queryKey: ['trackers', parcelNumber, zone, isLabeled, page],
    queryFn: async () => {
      let query = supabase
        .from('tracker_polygons')
        .select(`
          *,
          parcel:parcels(id, parcel_number, display_name)
        `)
        .range(page * pageSize, (page + 1) * pageSize - 1)

      if (parcelNumber !== null) {
        query = query.eq('parcel:parcels.parcel_number', parcelNumber)
      }
      if (zone !== null) {
        query = query.eq('zone', zone)
      }
      if (isLabeled !== null) {
        query = query.eq('is_labeled', isLabeled)
      }

      const { data, error } = await query
      if (error) throw new Error(error.message)
      return (data ?? []) as TrackerPolygon[]
    },
    staleTime: STALE_TIME_MS,
  })

  return {
    trackers:        trackersQuery.data ?? [],
    isLoading:       trackersQuery.isLoading,
    error:           trackersQuery.error,
    refetch:         trackersQuery.refetch,
  }
}

// =============================================================================
// useTracker — Single tracker detail
// =============================================================================

export function useTracker(trackerId: string | null) {
  return useQuery<TrackerPolygon | null>({
    queryKey: ['tracker', trackerId],
    queryFn: async () => {
      if (!trackerId) return null

      const { data, error } = await supabase
        .from('tracker_polygons')
        .select(`
          *,
          parcel:parcels(*),
          label_assignment:tracker_label_assignments(*)
        `)
        .eq('id', trackerId)
        .single()

      if (error) throw new Error(error.message)
      return data as TrackerPolygon
    },
    enabled:   !!trackerId,
    staleTime: STALE_TIME_MS,
  })
}

// =============================================================================
// useTrackerProgress — Progress for a specific tracker (by code)
// =============================================================================

export function useTrackerProgress(trackerCode: string | null) {
  return useQuery({
    queryKey: ['tracker-progress', trackerCode],
    queryFn: async () => {
      if (!trackerCode) return []

      const { data, error } = await supabase.rpc('get_tracker_progress', {
        p_tracker_code: trackerCode,
      })

      if (error) throw new Error(error.message)
      return data ?? []
    },
    enabled:   !!trackerCode,
    staleTime: STALE_TIME_MS,
  })
}

// =============================================================================
// useTrackersByParcel — All trackers for a parcel with activity progress
// =============================================================================

export function useTrackersByParcel(parcelId: string | null) {
  return useQuery({
    queryKey: ['trackers-by-parcel', parcelId],
    queryFn: async () => {
      if (!parcelId) return []

      const { data, error } = await supabase
        .from('tracker_polygons')
        .select(`
          id, tracker_code, zone, string_type, polygon_index,
          is_labeled, label_conflict, centroid
        `)
        .eq('parcel_id', parcelId)
        .eq('is_labeled', true)

      if (error) throw new Error(error.message)
      return data ?? []
    },
    enabled:   !!parcelId,
    staleTime: STALE_TIME_MS,
  })
}

// =============================================================================
// useUpdateTrackerProgress — Mutation to update progress
// =============================================================================

export function useUpdateTrackerProgress() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      activityInstanceId,
      templateId,
      reportedPct,
      notes,
    }: {
      activityInstanceId: string
      templateId:         string
      reportedPct:        number
      notes?:             string
    }) => {
      // Update subactivity progress
      const { error: updateError } = await supabase
        .from('subactivity_progress')
        .upsert({
          activity_instance_id: activityInstanceId,
          template_id:          templateId,
          reported_pct:         reportedPct,
          status:               reportedPct >= 100 ? 'FINALIZADA' : reportedPct > 0 ? 'EN_CURSO' : 'NO_INICIADA',
          updated_at:           new Date().toISOString(),
        }, { onConflict: 'activity_instance_id,template_id' })

      if (updateError) throw new Error(updateError.message)

      // Log progress
      const { data: { user } } = await supabase.auth.getUser()
      if (user) {
        await supabase.from('progress_logs').insert({
          activity_instance_id: activityInstanceId,
          template_id:          templateId,
          reported_by:          user.id,
          new_pct:              reportedPct,
          notes:                notes ?? null,
        })
      }
    },
    onSuccess: (_, vars) => {
      queryClient.invalidateQueries({ queryKey: ['tracker-progress'] })
      queryClient.invalidateQueries({ queryKey: ['activities'] })
      queryClient.invalidateQueries({ queryKey: ['dashboard'] })
    },
  })
}

// =============================================================================
// useTrackerGeoJSON — Fetch trackers as GeoJSON for MapLibre
// =============================================================================

export function useTrackerGeoJSON(parcelNumbers?: number[]) {
  return useQuery({
    queryKey: ['tracker-geojson', parcelNumbers],
    queryFn: async () => {
      let query = supabase
        .from('tracker_polygons')
        .select(`
          id, tracker_code, zone, string_type, polygon_index,
          is_labeled, label_conflict,
          parcel:parcels(parcel_number, display_name)
        `)

      // NOTE: Geometry is returned as GeoJSON via PostGIS
      // In production, enable the PostgREST geometry serialization
      // For now we return metadata; actual geometry loaded via parse_kmz output

      if (parcelNumbers && parcelNumbers.length > 0) {
        // This requires a join filter; simplified here
      }

      const { data, error } = await query.limit(7500)
      if (error) throw new Error(error.message)

      // Convert to GeoJSON FeatureCollection (geometry must be pre-loaded)
      return {
        type: 'FeatureCollection' as const,
        features: (data ?? []).map((t) => ({
          type: 'Feature' as const,
          id:   t.id,
          properties: {
            id:           t.id,
            tracker_code: t.tracker_code,
            zone:         t.zone,
            string_type:  t.string_type,
            is_labeled:   t.is_labeled,
            parcel_number: (t.parcel as { parcel_number: number } | null)?.parcel_number,
          },
          geometry: null, // geometry must be loaded from GeoJSON file
        })),
      }
    },
    staleTime: 10 * 60 * 1000, // Geometry is static; cache for 10 minutes
  })
}
